import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { BookMarked, Search, ArrowLeft, ChevronDown, X, Filter } from 'lucide-react';
import { apiService } from '../services/api';
import { cn } from '../utils/cn';

interface GlossaryEntry {
  id: string;
  term: string;
  definition: string;
  category?: string;
  aliases?: string[];
  tags?: string[];
  image?: string;
  isActive: boolean;
}

const GlossaryPage: React.FC = () => {
  const { term } = useParams<{ term?: string }>();
  const navigate = useNavigate();
  const [entries, setEntries] = useState<GlossaryEntry[]>([]);
  const [filteredEntries, setFilteredEntries] = useState<GlossaryEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [categories, setCategories] = useState<string[]>([]);
  const [categorySearchQuery, setCategorySearchQuery] = useState('');
  const [isCategoryDropdownOpen, setIsCategoryDropdownOpen] = useState(false);
  const categoryDropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    loadGlossary();
  }, []);

  // Close category dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (categoryDropdownRef.current && !categoryDropdownRef.current.contains(event.target as Node)) {
        setIsCategoryDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    filterEntries();
    // Scroll to term if specified in URL
    if (term) {
      setTimeout(() => {
        const element = document.getElementById(`term-${encodeURIComponent(term)}`);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'start' });
          element.classList.add('ring-2', 'ring-purple-500', 'ring-offset-2');
          setTimeout(() => {
            element.classList.remove('ring-2', 'ring-purple-500', 'ring-offset-2');
          }, 3000);
        }
      }, 500);
    }
  }, [searchQuery, selectedCategory, entries, term]);

  const loadGlossary = async () => {
    try {
      setLoading(true);
      const response = await apiService.getGlossary(true); // Only active entries
      if (response.success && response.data) {
        const parsedEntries = response.data.map((entry: any) => ({
          ...entry,
          aliases: typeof entry.aliases === 'string' ? JSON.parse(entry.aliases) : entry.aliases || [],
          tags: typeof entry.tags === 'string' ? JSON.parse(entry.tags) : entry.tags || []
        }));
        setEntries(parsedEntries);
        
        // Extract unique categories
        const uniqueCategories = Array.from(
          new Set(parsedEntries.map((e: GlossaryEntry) => e.category).filter(Boolean))
        ) as string[];
        setCategories(uniqueCategories.sort());
      }
    } catch (error) {
      console.error('Failed to load glossary:', error);
    } finally {
      setLoading(false);
    }
  };

  const filterEntries = () => {
    let filtered = entries;

    // Filter by category
    if (selectedCategory !== 'all') {
      filtered = filtered.filter(entry => entry.category === selectedCategory);
    }

    // Filter by search query
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(entry => {
        const termMatch = entry.term.toLowerCase().includes(query);
        const definitionMatch = entry.definition.toLowerCase().includes(query);
        const aliasMatch = entry.aliases?.some(alias => alias.toLowerCase().includes(query));
        return termMatch || definitionMatch || aliasMatch;
      });
    }

    // Sort alphabetically by term
    filtered.sort((a, b) => a.term.localeCompare(b.term));
    setFilteredEntries(filtered);
  };

  // Filter categories based on search
  const filteredCategories = categories.filter(category =>
    category.toLowerCase().includes(categorySearchQuery.toLowerCase())
  );

  // Get category count
  const getCategoryCount = (category: string) => {
    if (category === 'all') return entries.length;
    return entries.filter(e => e.category === category).length;
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading glossary...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-6xl mx-auto px-4">
        {/* Header */}
        <div className="mb-8">
          <button
            onClick={() => navigate(-1)}
            className="flex items-center text-gray-600 hover:text-gray-900 mb-4"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back
          </button>
          <div className="flex items-center space-x-3 mb-4">
            <div className="w-12 h-12 rounded-lg bg-purple-100 flex items-center justify-center">
              <BookMarked className="w-6 h-6 text-purple-600" />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Glossary</h1>
              <p className="text-gray-600 mt-1">
                {filteredEntries.length} {filteredEntries.length === 1 ? 'term' : 'terms'}
                {selectedCategory !== 'all' && ` in ${selectedCategory}`}
              </p>
            </div>
          </div>
        </div>

        {/* Search and Filters */}
        <div className="bg-white rounded-lg shadow-sm p-4 mb-6 space-y-4">
          <div className="flex flex-col sm:flex-row gap-4">
            {/* Search Input */}
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <input
                type="text"
                placeholder="Search terms, definitions, or aliases..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              />
            </div>
            
            {/* Category Dropdown */}
            {categories.length > 0 && (
              <div className="relative" ref={categoryDropdownRef}>
                <button
                  onClick={() => {
                    setIsCategoryDropdownOpen(!isCategoryDropdownOpen);
                    setCategorySearchQuery('');
                  }}
                  className={cn(
                    "flex items-center justify-between w-full sm:w-64 px-4 py-2.5 border rounded-lg text-sm font-medium transition-colors",
                    selectedCategory !== 'all'
                      ? "bg-purple-50 border-purple-300 text-purple-700"
                      : "bg-white border-gray-300 text-gray-700 hover:bg-gray-50"
                  )}
                >
                  <div className="flex items-center space-x-2">
                    <Filter className="w-4 h-4" />
                    <span className="truncate">
                      {selectedCategory === 'all' 
                        ? `All Categories (${getCategoryCount('all')})`
                        : `${selectedCategory} (${getCategoryCount(selectedCategory)})`
                      }
                    </span>
                  </div>
                  <ChevronDown className={cn(
                    "w-4 h-4 transition-transform",
                    isCategoryDropdownOpen && "transform rotate-180"
                  )} />
                </button>

                {/* Dropdown Menu */}
                {isCategoryDropdownOpen && (
                  <div className="absolute z-50 w-full sm:w-80 mt-2 bg-white border border-gray-200 rounded-lg shadow-lg max-h-96 overflow-hidden flex flex-col">
                    {/* Category Search */}
                    <div className="p-3 border-b border-gray-200">
                      <div className="relative">
                        <Search className="absolute left-2 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                        <input
                          type="text"
                          placeholder="Search categories..."
                          value={categorySearchQuery}
                          onChange={(e) => setCategorySearchQuery(e.target.value)}
                          onClick={(e) => e.stopPropagation()}
                          className="w-full pl-8 pr-8 py-2 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                        />
                        {categorySearchQuery && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setCategorySearchQuery('');
                            }}
                            className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    </div>

                    {/* Category List */}
                    <div className="overflow-y-auto flex-1">
                      <button
                        onClick={() => {
                          setSelectedCategory('all');
                          setIsCategoryDropdownOpen(false);
                          setCategorySearchQuery('');
                        }}
                        className={cn(
                          "w-full px-4 py-2.5 text-left text-sm transition-colors flex items-center justify-between",
                          selectedCategory === 'all'
                            ? "bg-purple-50 text-purple-700 font-medium"
                            : "hover:bg-gray-50 text-gray-700"
                        )}
                      >
                        <span>All Categories</span>
                        <span className="text-xs text-gray-500 ml-2">({getCategoryCount('all')})</span>
                      </button>
                      {filteredCategories.length > 0 ? (
                        filteredCategories.map(category => (
                          <button
                            key={category}
                            onClick={() => {
                              setSelectedCategory(category);
                              setIsCategoryDropdownOpen(false);
                              setCategorySearchQuery('');
                            }}
                            className={cn(
                              "w-full px-4 py-2.5 text-left text-sm transition-colors flex items-center justify-between",
                              selectedCategory === category
                                ? "bg-purple-50 text-purple-700 font-medium"
                                : "hover:bg-gray-50 text-gray-700"
                            )}
                          >
                            <span className="truncate">{category}</span>
                            <span className="text-xs text-gray-500 ml-2">({getCategoryCount(category)})</span>
                          </button>
                        ))
                      ) : (
                        <div className="px-4 py-8 text-center text-sm text-gray-500">
                          No categories found
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Active Filter Badge */}
          {selectedCategory !== 'all' && (
            <div className="flex items-center space-x-2">
              <span className="text-sm text-gray-600">Filtered by:</span>
              <button
                onClick={() => setSelectedCategory('all')}
                className="inline-flex items-center space-x-1 px-3 py-1 bg-purple-100 text-purple-700 rounded-full text-sm font-medium hover:bg-purple-200 transition-colors"
              >
                <span>{selectedCategory}</span>
                <X className="w-3 h-3" />
              </button>
            </div>
          )}
        </div>

        {/* Glossary Entries */}
        {filteredEntries.length === 0 ? (
          <div className="text-center py-12 bg-white rounded-lg shadow-sm">
            <BookMarked className="w-12 h-12 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No terms found</h3>
            <p className="text-gray-600">
              {searchQuery ? 'Try a different search term' : 'No glossary entries available'}
            </p>
          </div>
        ) : (
          <div className="space-y-3">
            {filteredEntries.map((entry) => (
              <div
                key={entry.id}
                id={`term-${encodeURIComponent(entry.term)}`}
                className="bg-white rounded-lg border border-gray-200 p-6 hover:border-purple-300 hover:shadow-md transition-all"
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between mb-3">
                      <h3 className="text-xl font-bold text-gray-900 pr-4">{entry.term}</h3>
                      {entry.category && (
                        <span className="px-3 py-1 bg-purple-100 text-purple-700 rounded-full text-xs font-semibold whitespace-nowrap flex-shrink-0">
                          {entry.category}
                        </span>
                      )}
                    </div>
                    
                    {entry.aliases && entry.aliases.length > 0 && (
                      <div className="mb-4">
                        <span className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-2 block">
                          Also known as
                        </span>
                        <div className="flex flex-wrap gap-2">
                          {entry.aliases.map((alias, idx) => (
                            <span
                              key={idx}
                              className="px-3 py-1.5 bg-gray-50 border border-gray-200 text-gray-700 rounded-md text-sm font-medium"
                            >
                              {alias}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                    
                    <p className="text-gray-700 leading-relaxed text-base mb-4">{entry.definition}</p>
                    
                    {entry.tags && entry.tags.length > 0 && (
                      <div className="flex flex-wrap gap-2 pt-3 border-t border-gray-100">
                        {entry.tags.map((tag, idx) => (
                          <span
                            key={idx}
                            className="px-2.5 py-1 bg-gray-50 text-gray-600 rounded-md text-xs font-medium"
                          >
                            {tag}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default GlossaryPage;

